import { useAuth } from "@/auth/useAuth";
import {
  MutationCache,
  MutationKey,
  QueryCache,
  QueryClientProvider,
  QueryKey,
  QueryClient as ReactQueryClient,
} from "@tanstack/react-query";
import { PropsWithChildren, useCallback, useMemo } from "react";
import { getFetchErrorType, isUnauthorized } from "@/lib/errors";
import { logError } from "@/lib/logging";
import "@tanstack/react-query";
import { getI18n } from "react-i18next";
import { toast } from "sonner";

interface CustomMetadata extends Record<string, unknown> {
  token?: string;
}

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: CustomMetadata;
    mutationMeta: CustomMetadata;
  }
}

const THIRTY_MINUTES = 1000 * 60 * 30;
const RETRY_LIMIT = import.meta.env.MODE === "test" ? 0 : 3;

export function QueryClient({ children }: PropsWithChildren) {
  const { token, logout } = useAuth();

  const handleError = useCallback(
    async (error: Error, key?: QueryKey | MutationKey) => {
      logError(getFetchErrorType(error), {
        error,
        key,
      });

      if (isUnauthorized(error)) {
        logout();
        toast.warning(getI18n().t("login.sessionExpiredToast"), {
          id: "authError",
        });
      }
    },
    [logout]
  );

  const queryClient = useMemo(
    () =>
      new ReactQueryClient({
        defaultOptions: {
          queries: {
            meta: {
              token,
            },
            staleTime: THIRTY_MINUTES,
            retry: (failureCount, error) =>
              !isUnauthorized(error) && failureCount < RETRY_LIMIT,
          },
        },
        queryCache: new QueryCache({
          onError: async (error, query) => {
            await handleError(error, query.queryKey);
          },
        }),
        mutationCache: new MutationCache({
          onError: async (error, _vars, __ctx, mutation) => {
            await handleError(error, mutation.options.mutationKey);
          },
        }),
      }),
    [handleError, token]
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
